import { useConfig } from '#~/config/hooks.js';
import { readSessionReturn, writeSessionReturn } from './session-return.js';
import { authStore } from './store.js';
import { isElectronApp } from '#~/electron/utils.js';
import { createAuthJourneyTracker } from '#~/analytics/domains/auth-journey.js';
import { trackSignIn } from '#~/analytics/domains/sign-out-lifecycle.js';
import type { AnalyticsTrackFn } from '#~/analytics/domains/types.js';
import { track as trackAnalytics } from '#~/analytics/track.js';
import type { AnalyticsEvent } from '#~/analytics/types/index.js';
import type { AppState, User } from '@auth0/auth0-react';
import { Auth0Provider, useAuth0 } from '@auth0/auth0-react';
import { Loader } from '@studio/design-system/loader';
import { useLatestFn } from '@studio/react-utils/hooks';
import { type ReactElement, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  type AuthJourneyAppState,
  clearActiveAuthJourney,
  getActiveAuthJourney,
  getAuthJourneyFromAppState,
  getLastKnownUserId,
  setActiveAuthJourney
} from './auth-journey.js';
import { isDesktopAppAuthCallback } from './app-auth-callback.js';
import { buildCollisionPath } from './collision.js';
import { Synchronizer } from './synchronizer.jsx';
import type { Props } from './types.js';

// Module-level tracker — adapter from (name, category, props) → AnalyticsEvent.
// Per ENG-3960 W6: wires oauth_error to the auth-journey tracker domain.
const authJourneyTracker = createAuthJourneyTracker((name, category, properties) => {
  void trackAnalytics({ category, name, properties } as AnalyticsEvent);
});
const trackSignInEvent: AnalyticsTrackFn = (name, category, properties) => {
  void trackAnalytics({ category, name, properties } as AnalyticsEvent);
};

// Renders nothing; detects Auth0 redirect errors via useAuth0().error and fires
// oauth_error with deferred attribution (ENG-3479: last_known_user_id resolves
// the event to the previously authenticated user even in a logged-out session).
// Must be a child of Auth0Provider to access the auth context.
// Per rule verify-and-escalate: only Auth0-controlled fields are logged — never tokens.
const OAuthErrorTracker = (): null => {
  const { error } = useAuth0();

  useEffect(() => {
    if (!error) return;

    const journey = getActiveAuthJourney();
    const lastUserId = getLastKnownUserId();
    // Auth0 OAuthError carries .error (code e.g. 'access_denied') and
    // .error_description (human-readable). Fall back to standard Error fields.
    // Never log tokens or raw credential material — only Auth0-controlled strings.
    const oauthErr = error as Error & { error?: string; error_description?: string };

    authJourneyTracker.trackOAuthError(journey, {
      provider: 'auth0',
      error_type: oauthErr.error ?? oauthErr.name,
      error_message: oauthErr.error_description ?? oauthErr.message,
      ...(lastUserId ? { last_known_user_id: lastUserId } : {})
    });
  }, [error]);

  return null;
};

/** Collision page path for an Auth0 redirect error, or null when it is not a collision. */
const collisionPathFor = (error: Error | undefined): string | null => {
  if (!error) return null;

  const { error: code, error_description: description } = error as Error & {
    error?: string;
    error_description?: string;
  };

  return buildCollisionPath({
    ...(code ? { error: code } : {}),
    ...(description ? { errorDescription: description } : {})
  });
};

/**
 * <Synchronizer> throws ERR-734E on any Auth0 error. An account-collision deny
 * is an expected outcome with its own public page, so it never reaches
 * Synchronizer: the page loads fresh, which also leaves the Auth0 error behind.
 */
const SignInOutcomeGate = ({ children }: Pick<Props, 'children'>): ReactElement => {
  const { error } = useAuth0();
  const collisionPath = collisionPathFor(error);

  useEffect(() => {
    if (collisionPath) {
      // replace() keeps the failed callback URL out of history.
      window.location.replace(collisionPath);
    }
  }, [collisionPath]);

  if (collisionPath) {
    return <Loader />;
  }

  return <Synchronizer>{children}</Synchronizer>;
};

/** Authentication `<Provider>` component. */
export const Provider = ({ callbackUrl, children }: Props): ReactElement => {
  const config = useConfig();
  const navigate = useNavigate();

  const handleRedirect = useLatestFn((state?: AppState, user?: User) => {
    const appState = state as AuthJourneyAppState | undefined;
    const authJourney = getAuthJourneyFromAppState(state);

    if (authJourney) {
      setActiveAuthJourney(authJourney);

      // ENG-4330: the shared tracker, not an inline event — the Electron login
      // path fires the same helper, so the web and desktop sign-in stages carry
      // an identical property shape and differ only by the `client` envelope.
      authJourneyTracker.trackLoginSucceeded(authJourney);
    } else {
      clearActiveAuthJourney();
    }

    if (isElectronApp()) {
      // Electron app should not redirect to the callback URL
      return;
    }

    // Auth0 invokes this only after a successful redirect. Protected-route
    // redirects need not have a funnel journey, but still complete a sign-in.
    trackSignIn(trackSignInEvent);

    const resume = readSessionReturn();
    // The callback supplies the newly authenticated identity before the SDK
    // publishes it to React. Recovery belongs only to that same account.
    const returnTo = resume
      ? resume.userId === user?.sub
        ? resume.returnTo
        : '/'
      : (appState?.returnTo ?? window.location.pathname);
    writeSessionReturn(undefined);
    authStore.setState({ sessionEnded: undefined });
    navigate(returnTo, {
      replace: true,
      ...(appState?.fromRegistration ? { state: { fromRegistration: true } } : {})
    });
  });

  // determine if path contains the query param ?desktopAuth=true
  const desktopAuth = window.location.search.includes('desktopAuth=true') || isElectronApp();

  if (desktopAuth) {
    return <Synchronizer>{children}</Synchronizer>;
  }

  // ENG-4637 blocker: on the desktop app-auth callback the authorization code
  // belongs to the DESKTOP client. Mounting Auth0Provider here would redeem it
  // with the WEB client id (403 invalid_grant), and the resulting
  // `useAuth0().error` makes <Synchronizer> throw ERR-734E during render — so
  // <AppAuth>, the component that fires the `iris://code/<code>` deep link,
  // never mounts. Rendering the route bare settles both halves at once: there
  // is no competing exchange, and the deep-link hand-off no longer sits behind
  // a token POST it cannot influence — it fires on the first render.
  // See auth/app-auth-callback.ts for the full mechanism.
  if (isDesktopAppAuthCallback(window.location)) {
    return <>{children}</>;
  }

  const clientId = desktopAuth ? config.oauth.clientIdDesktop : config.oauth.clientIdWeb;

  const domain = new URL(config.frontendUrl).host;

  return (
    <Auth0Provider
      useRefreshTokens
      authorizationParams={{
        audience: config.oauth.audience,
        redirect_uri: `https://${domain}${callbackUrl}`,
        scope: 'openid profile email'
      }}
      clientId={clientId}
      domain={config.oauth.url}
      onRedirectCallback={handleRedirect}
    >
      {/* Fires oauth_error on Auth0 redirect failures; see ENG-3960 W6. */}
      <OAuthErrorTracker />
      <SignInOutcomeGate>{children}</SignInOutcomeGate>
    </Auth0Provider>
  );
};
